/**
 * DOM Utilities - Shared helper functions for DOM manipulation
 */

type AnyFunction = (...args: any[]) => any;

export function throttle<T extends AnyFunction>(func: T, limit: number): T {
    let inThrottle = false;
    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    } as T;
}

export function debounce<T extends AnyFunction>(func: T, wait: number): T {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    } as T;
}

export function isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

export function getScrollPosition(): number {
    return window.scrollY || document.documentElement.scrollTop;
}

export function matchMedia(query: string): boolean {
    return window.matchMedia(query).matches;
}

export const BREAKPOINTS = {
    sm: 576,
    md: 768,
    lg: 960,
    xl: 1200
} as const;

type BreakpointKey = keyof typeof BREAKPOINTS;

export function isBelow(size: BreakpointKey | number): boolean {
    const breakpoint = typeof size === 'string' ? BREAKPOINTS[size] : size;
    return window.innerWidth < breakpoint;
}

export function isAbove(size: BreakpointKey | number): boolean {
    const breakpoint = typeof size === 'string' ? BREAKPOINTS[size] : size;
    return window.innerWidth >= breakpoint;
}

let scrollLockCount = 0;

// While the background is scroll-locked we still must allow touch scrolling
// inside overlay panels that have their own scroll (e.g. the open mobile menu,
// the search overlay). Block the touchmove only when it isn't over a genuinely
// scrollable ancestor — otherwise the panel's scrollbar shows but can't move.
const preventScroll = (e: Event): void => {
    let el = e.target as HTMLElement | null;

    while (el && el !== document.body) {
        const overflowY = getComputedStyle(el).overflowY;
        const isScrollable = (overflowY === 'auto' || overflowY === 'scroll')
            && el.scrollHeight > el.clientHeight;

        if (isScrollable) {
            return;
        }

        el = el.parentElement;
    }

    e.preventDefault();
};

export function setBodyScrollLock(locked: boolean): void {
    if (locked) {
        scrollLockCount++;
        if (scrollLockCount === 1) {
            document.body.style.overflow = 'hidden';
            document.body.addEventListener('touchmove', preventScroll, { passive: false });
        }
    } else {
        scrollLockCount = Math.max(0, scrollLockCount - 1);
        if (scrollLockCount === 0) {
            document.body.style.overflow = '';
            document.body.removeEventListener('touchmove', preventScroll);
        }
    }
}

export function onDomReady(callback: () => void): void {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback);
    } else {
        callback();
    }
}

interface ElementAttributes {
    className?: string;
    dataset?: Record<string, string>;
    [key: string]: any;
}

export function createElement(
    tag: string,
    attrs: ElementAttributes = {},
    children: (string | HTMLElement)[] | string = []
): HTMLElement {
    const el = document.createElement(tag);

    Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'className') {
            el.className = value;
        } else if (key === 'dataset') {
            Object.entries(value as Record<string, string>).forEach(([dataKey, dataValue]) => {
                el.dataset[dataKey] = dataValue;
            });
        } else if (key.startsWith('on') && typeof value === 'function') {
            el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
        } else {
            el.setAttribute(key, value);
        }
    });

    if (typeof children === 'string') {
        el.textContent = children;
    } else if (Array.isArray(children)) {
        children.forEach(child => {
            if (typeof child === 'string') {
                el.appendChild(document.createTextNode(child));
            } else if (child instanceof HTMLElement) {
                el.appendChild(child);
            }
        });
    }

    return el;
}

type DelegateHandler = (this: Element, event: Event, target: Element) => void;

/**
 * @returns Cleanup function to remove the event listener
 */
export function delegate(
    parent: HTMLElement | Document,
    eventType: string,
    selector: string,
    handler: DelegateHandler
): () => void {
    const listener = (e: Event): void => {
        const target = (e.target as Element).closest(selector);
        if (target && parent.contains(target)) {
            handler.call(target, e, target);
        }
    };

    parent.addEventListener(eventType, listener);
    return () => parent.removeEventListener(eventType, listener);
}

/**
 * @returns Promise that resolves when animation completes
 */
export function smoothScrollTo(targetTop: number, duration: number = 400): Promise<void> {
    return new Promise((resolve) => {
        const startTop = window.scrollY;
        const distance = targetTop - startTop;
        let startTime: number | null = null;

        const animation = (currentTime: number): void => {
            if (!startTime) {
                startTime = currentTime;
            }

            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const ease = progress < 0.5
                ? 2 * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;

            window.scrollTo(0, startTop + distance * ease);

            if (timeElapsed < duration) {
                requestAnimationFrame(animation);
            } else {
                resolve();
            }
        };

        requestAnimationFrame(animation);
    });
}

interface EqualizeHeightsOptions {
    minWidth?: number;
    watchResize?: boolean;
    selector?: string | null;
    measureSelector?: string | null;
    property?: 'height' | 'minHeight';
}

interface EqualizeCleanup {
    (): void;
    run: () => void;
}

/**
 * @returns Cleanup function with a `.run()` method to manually re-equalize
 */
export function equalizeHeights(
    elements: string | NodeListOf<HTMLElement> | HTMLElement[] | HTMLElement,
    options: EqualizeHeightsOptions = {}
): EqualizeCleanup {
    const { minWidth = 0, watchResize = false, selector = null, measureSelector = null, property = 'height' } = options;

    const getElements = (): NodeListOf<HTMLElement> | HTMLElement[] | null => {
        if (elements instanceof HTMLElement && selector) {
            return elements.querySelectorAll<HTMLElement>(selector);
        }
        if (typeof elements === 'string') {
            return document.querySelectorAll<HTMLElement>(elements);
        }
        return elements as NodeListOf<HTMLElement> | HTMLElement[];
    };

    const getMeasureElements = (): NodeListOf<HTMLElement> | null => {
        if (!measureSelector) {
            return null;
        }
        if (elements instanceof HTMLElement) {
            return elements.querySelectorAll<HTMLElement>(measureSelector);
        }
        return document.querySelectorAll<HTMLElement>(measureSelector);
    };

    const equalize = (): void => {
        const els = getElements();
        if (!els || (els as any).length === 0) {
            return;
        }

        (els as HTMLElement[]).forEach(el => el.style[property] = '');

        if (window.innerWidth < minWidth) {
            return;
        }

        const measureEls = getMeasureElements() || els;
        let maxHeight = 0;
        (measureEls as HTMLElement[]).forEach(el => {
            const height = el.offsetHeight;
            if (height > maxHeight) maxHeight = height;
        });

        if (maxHeight > 0) {
            (els as HTMLElement[]).forEach(el => el.style[property] = maxHeight + 'px');
        }
    };

    equalize();

    let resizeHandler: (() => void) | null = null;
    if (watchResize) {
        resizeHandler = debounce(equalize, 150);
        window.addEventListener('resize', resizeHandler);
    }

    const cleanup = (): void => {
        const els = getElements();
        if (els) {
            (els as HTMLElement[]).forEach(el => el.style[property] = '');
        }
        if (resizeHandler) {
            window.removeEventListener('resize', resizeHandler);
        }
    };

    (cleanup as EqualizeCleanup).run = equalize;

    return cleanup as EqualizeCleanup;
}